import React, { useEffect } from "react";
import { Dynamic } from "../../../../../controllers/conductor";
import { Area, TriggerSet } from "../../../../../services/map";

import "../../../../../styles/pages/dashboard.css";
import { Button, IconButton } from "../../../../widgets/buttons";
import { MonthYearSelector } from "../../../../widgets/date_selector";
import { Dropdown } from "../../../../widgets/input";
import { LoadingAnimation } from "../../../../widgets/loading";


const canvasHeight = 450;
const canvasWidth = 600;


/** @param {{ controller: SamedayDashboardController }} param0 */
export function MapSection({ controller }) {
    const { notifiers } = controller;

    const dropdownOptions = [
        { label: "Domestic", value: "domestic" },
        { label: "Foreign", value: "foreign" },
        { label: "Total", value: "all" },
    ];

    const subcontroller = { onHover: () => {} };


    return <Dynamic conductor={[ notifiers.mapLoading, notifiers.mapError ]} child={()=>{
        const { data: { map: data }, status, reference, mapController } = controller;
        const { province, completed, children } = data;
        const { month, year } = reference.map;

        const totals = {
            domestic: children.reduce((a, b) => a + (b.total?.domestic || 0), 0),
            foreign: children.reduce((a, b) => a + (b.total?.foreign || 0), 0),
            all: children.reduce((a, b) => a + (b.total?.all || 0), 0),
        };

        const onHover = (id) => subcontroller.onHover(id);
        const zoom = (direction) => {
            const multiplier = 1.4;
            if(direction === "in") mapController.transform.zoomFactor *= multiplier;
            else mapController.transform.zoomFactor /= multiplier;
            mapController.render();
        };

        return <section className="map-view" style={{ height: canvasHeight }}>
            <div className="map-wrapper">
                <Dynamic conductor={[ notifiers.mapRefresh ]} child={()=>
                controller.status.mapLoading ? 
                    <div className="map-innerwrapper">
                        <div style={{ height: canvasHeight, width: canvasWidth }} >
                            <center><LoadingAnimation />Loading map...</center>
                        </div>
                    </div> : 
                    <MapView controller={controller} subcontroller={subcontroller} />} />
                <div className="controls">
                    <div>
                        <MonthYearSelector month={month} year={year} onChange={date => controller.changeMapDate(date)} /><br />
                        <Dropdown options={dropdownOptions} onSelect={category => controller.setMapCategory(category)} /><br />
                        <Button onClick={() => controller.downloadMap()} icon="save">Download</Button>
                    </div>
                    <div>
                        <Button className="zoom-btn out" onClick={() => zoom("out")}></Button>
                        <Button className="zoom-btn in" onClick={() => zoom("in")}></Button>
                    </div>
                </div>
            </div>
            { controller.status.mapLoading ? <center><LoadingAnimation /></center> :
            <div className="menu">
                <header>
                    <div className="title">Geographic Visualization</div>
                    { province ? <div className="navigator">
                        <IconButton icon="back" onClick={() => controller.undoMap()} />
                        <div className="province-name">{province.name}</div>
                    </div> : [] }
                    <div className="totals">
                        <div className="total completed">
                            <div className="label">PUBLISHED</div>
                            <div className="value">{completed}</div>
                        </div>
                        <div className="total">
                            <div className="label">DOMESTIC</div>
                            <div className="value">{totals.domestic}</div>
                        </div>
                        <div className="total">
                            <div className="label">FOREIGN</div>
                            <div className="value">{totals.foreign}</div>
                        </div>
                        <div className="total all">
                            <div className="label">TOTAL</div>
                            <div className="value">{totals.all}</div>
                        </div>
                    </div>
                </header>
                <main onMouseLeave={() => onHover(null)}>
                    { children.map((child, i) => {
                        return <div key={i} onMouseMove={() => onHover(child.id)} onClick={() => !province ? controller.expandMapProvince(child.id) : null} className="child">
                            <div className="left">
                                <div className="name">{child.name}</div>
                                <div className="data-row">
                                    { child.total ?  [
                                        <div key="1" className="data-column domestic">{child.total.domestic}</div>,
                                        <div key="2" className="data-column foreign">{child.total.foreign}</div>,
                                        <div key="3" className="data-column all">{child.total.all}</div> ] : [] }

                                    { !child.total ? <div key="1" className="data-column nodata">NO DATA</div> : [] }
                                </div>

                            </div>
                            <div className="right"></div>
                        </div>;
                    }) }
                </main>
            </div> }
        </section> ;
    }} />;
    
}

/** @param {{ controller: SamedayDashboardController }} param0 */
function MapView({ controller, subcontroller }) {

    const { data: { map: data, coords }, mapController, reference: { map: { category, province } } } = controller;
    const { month, year, children } = data;

    const valueColors = {
        domestic: [ "#5484E1", "#689CEB", "#8DB0F2", "#A5C3FF", "#B8D0FD" ],
        foreign: [ "#309D4E", "#37BB5C", "#50CE73", "#77D692", "#9DDDAF" ],
        all: [ "#EF8E35", "#FB9E48", "#FDB068", "#FFBF85", "#FECDA0" ]
    };

    const totals = {
        domestic: children.reduce((a, b) => a + (b.total?.domestic || 0), 0),
        foreign: children.reduce((a, b) => a + (b.total?.foreign || 0), 0),
        all: children.reduce((a, b) => a + (b.total?.all || 0), 0),
    };

    function getFillColor(value) {
        const total = totals[category];
        const fraction = total ? value / total : 0;
        const colors = valueColors[category];

        console.log({ value, total: totals[category] });

        if(fraction >= 0.8) return colors[0];
        if(fraction >= 0.6) return colors[1];
        if(fraction >= 0.4) return colors[2];
        if(fraction >= 0.15) return colors[3];
        return colors[4];
    }

    const refs = {
        canvas: React.createRef(),
        container: React.createRef(),
        triggers: React.createRef()
    }



    const cards = [];
    const cardRefs = {};
    const areas = {};
    let i = 0;

    for(const child of children) {
        const { id, name, total } = child;
        const ref = React.createRef();

        const card = <div ref={ref} key={i++} className="card">
            <div className="name">{name}</div>
            <div className="stats">
                {
                    total ? [
                        <div key="1" className="stats-item">
                            <div className="label">DOMESTIC</div>
                            <div className="value">{total.domestic}</div>
                        </div>,
                        <div key="2" className="stats-item">
                            <div className="label">FOREIGN</div>
                            <div className="value">{total.foreign}</div>
                        </div>,
                        <br key="3" />,
                        <div key="4" className="stats-item total">
                            <div className="label">TOTAL</div>
                            <div className="value">{total.all}</div>
                        </div>,
                    ] : <div className="nodata">NO DATA</div>
                }
            </div>
        </div>;

        cards.push(card);
        cardRefs[id] = ref;
    }

    const defaultStyle = { stroke: { width: 3, color: "#ffffff" } };
    const activeStyle = { stroke: { color: "#555" } };

    subcontroller.onHover = (activeId) => {
        for(const id in areas) {
            if(id === activeId) {
                areas[id]?.setStyle(activeStyle);
                cardRefs[id]?.current?.classList.add("idle");
                mapController.highlightComponent = areas[id];
            } else {
                areas[id]?.setStyle(defaultStyle);
                cardRefs[id]?.current?.classList.remove("idle");
            }
            mapController.render({ paintOnly: true });
        }
    };

    useEffect(() => {
        mapController.canvas = refs.canvas.current;
        mapController.componentsContainer = refs.container.current;
        mapController.triggersContainer = refs.triggers.current;

        while(mapController.components.length > 0) mapController.components.pop();

        if(!province) {
            for(const provinceId in coords.provinces) {
                const provinceData = children.find(child => child.id === provinceId);
                const element = cardRefs[provinceId]?.current;

                const area = new Area({
                    coordinates: coords.provinces[provinceId].coords,
                    trigger: new TriggerSet({
                        onClick: () => controller.expandMapProvince(provinceId),
                        onHover: (component, position) => {
                            component.setStyle(activeStyle);
                            element?.classList.add("visible");
                            mapController.highlightComponent = component;
                            mapController.render({ paintOnly: true });
                        },
                        onHoverMove: (component, position) => {
                            element.style.left = position.x + "px";
                            element.style.top = position.y + "px";
                        },
                        onHoverOut: (component) => {
                            component.setStyle(defaultStyle);
                            element?.classList.remove("visible");
                            mapController.render({ paintOnly: true });
                        },
                    }),
                    style: {
                        ...defaultStyle,
                        fillColor: provinceData.total ? getFillColor(provinceData.total[category]) : "#FFCDCD"
                    }
                });

                areas[provinceId] = area;
                mapController.components.push(area);
            }
        } else {
            for(const provinceId in coords.provinces) {
                if(provinceId !== province) {
                    const area = new Area({
                        coordinates: coords.provinces[provinceId].coords,
                        style: {
                            ...defaultStyle,
                            fillColor: "#EEEEEE"
                        }
                    });


                    mapController.components.push(area);
                }

                for(const lguId in coords.lgus[province].lgus) {
                    const lguData = children.find(child => child.id === lguId);
                    if(!lguData) console.log({ lguId, children });
                    
                    const element = cardRefs[lguId]?.current;
                    element?.classList.remove("visible");

                    const area = new Area({
                        coordinates: coords.lgus[province].lgus[lguId].coords,
                        trigger: new TriggerSet({
                            onHover: (component, position) => {
                                component.setStyle(activeStyle);
                                element.style.left = position.x + "px";
                                element.style.top = position.y + "px";
                                element?.classList.add("visible");
                                mapController.highlightComponent = component;

                                mapController.render({ paintOnly: true });
                            },
                            onHoverMove: (component, position) => {
                                element.style.left = position.x + "px";
                                element.style.top = position.y + "px";
                            },
                            onHoverOut: (component) => {
                                component.setStyle(defaultStyle);
                                element?.classList.remove("visible");
                                mapController.render({ paintOnly: true });
                            },
                        }),
                        style: {
                            ...defaultStyle,
                            fillColor: lguData.total ? getFillColor(lguData.total[category]) : "#FFCDCD"
                        }
                    });

                    areas[lguId] = area;
                    mapController.components.push(area);
                }
            }
        }

        mapController.render();
    });


    return <div className="map-innerwrapper">
        <div className="container" ref={refs.container} style={{ height: canvasHeight, width: canvasWidth }}>
            <svg className="triggers" ref={refs.triggers} height={canvasHeight} width={canvasWidth} />
            { cards }
        </div>
        <canvas ref={refs.canvas} height={canvasHeight} width={canvasWidth}/>
    </div>;
}